import { isWebDataSuccess, VehiclesRequester, type WebData } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';
import type { Vehicle } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { selectDevices } from '@cobliteam/store';
import {
  loadVehiclesFailed,
  loadVehiclesSucceeded,
  loadVehiclesWithOdometerFailed,
  loadVehiclesWithOdometerSucceeded,
  openEtaPageFailed,
  openEtaPageRequest,
  openEtaPageSucceeded,
  updateVehicleTrackingFailed,
  updateVehicleTrackingSucceeded,
} from '@cobliteam/store/src/Vehicles/actionCreators';
import type { OpenEtaPageRequest, UpdateVehicleTrackingRequest } from '@cobliteam/store/src/Vehicles/actions';
import { VehicleActionTypes } from '@cobliteam/store/src/Vehicles/vehicleActionTypes';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { toast } from 'sonner';

const env = Environment.getInstance().getEnvironment();
const vehicleRequester = new VehiclesRequester({ host: env.herbieApiHost });
const vehicleGraphQLRequester = new GrahpQLVehicleRequester();

export function* requestLoadVehiclesSaga() {
  try {
    const vehicles: Vehicle[] = yield call(vehicleGraphQLRequester.getVehicles, false, false);
    yield put(loadVehiclesSucceeded(vehicles));
  } catch (err) {
    yield put(loadVehiclesFailed());
    toast.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestLoadVehiclesWithOdometerSaga() {
  try {
    const vehicles: Vehicle[] = yield call(vehicleGraphQLRequester.getVehicles, true, true);
    yield put(loadVehiclesWithOdometerSucceeded(vehicles));
  } catch (err) {
    yield put(loadVehiclesWithOdometerFailed());
    toast.error('Houve um erro, tente novamente mais tarde.');
  }
}

export function* requestOpenEtaPageSaga(action: OpenEtaPageRequest) {
  try {
    const { destination, esn } = action.payload;
    if (!esn) {
      throw new Error('Nenhum veículo vinculado á essa atividade.');
    }

    const devicesWebData: WebData<DeviceFragment[]> = yield select(selectDevices);
    if (isWebDataSuccess(devicesWebData)) {
      const device: DeviceFragment = devicesWebData.data.filter((deviceIt) => deviceIt.id === esn)[0];
      if (!device) {
        throw new Error('Não foi possível encontrar o veículo associado!');
      }

      const { tracking } = device;

      const trackingUrl: string = tracking ? `https://estouchegando.cobli.co/?${tracking.tempGeofenceId}` : yield call(vehicleRequester.createEtaPage, esn, destination);
      window.open(trackingUrl, '_blank');

      yield put(openEtaPageSucceeded());
      toast.success('Página de acompanhamento gerada com sucesso!');
    } else {
      throw new Error('Não foi possível carregar as informações dos veículos');
    }
  } catch (err) {
    yield put(openEtaPageFailed());
    if (err instanceof Error) {
      toast.error(err.message || 'Houve um erro ao abrir a página de acompanhamento, tente novamente mais tarde.');
    }
  }
}

export function* requestUpdateVehicleTrackingSaga(action: UpdateVehicleTrackingRequest) {
  try {
    const { deviceId, destination, isTracking } = action.payload;

    yield call(vehicleRequester.createEtaPage, deviceId, destination, isTracking);
    yield put(updateVehicleTrackingSucceeded());
    yield put(openEtaPageRequest(destination, deviceId));
    toast.success('Acompanhamento iniciado!');
  } catch (err) {
    yield put(updateVehicleTrackingFailed());
    toast.error('Houve um erro ao acompanhar o veículo, tente novamente mais tarde.');
  }
}

export function* VehicleSagasListener() {
  yield all([
    takeLatest(VehicleActionTypes.LOAD_VEHICLES_REQUEST, requestLoadVehiclesSaga),
    takeLatest(VehicleActionTypes.LOAD_VEHICLES_WITH_ODOMETER_REQUEST, requestLoadVehiclesWithOdometerSaga),
    takeLatest(VehicleActionTypes.OPEN_ETA_PAGE_REQUEST, requestOpenEtaPageSaga),
    takeLatest(VehicleActionTypes.UPDATE_VEHICLE_TRACKING_REQUEST, requestUpdateVehicleTrackingSaga),
  ]);
}
